/**
 * buffetUtils.js — Pure helpers for the Sunday dosa buffet board.
 *
 * Every dosa is three independent choices, which is why 16 names collapse to a
 * small picker:
 *   - flavor: plain | podi | karampodi | karam   (one of)
 *   - ghee:   on/off
 *   - onion:  on/off
 *
 * The canonical name is `[ghee] [flavor unless plain] [onion] dosa`, with the
 * special case "plain dosa" when nothing is selected. This reproduces all 16
 * menu names exactly.
 *
 * All functions are pure (no React, no Firestore) and unit-tested.
 */

export const DOSA_FLAVORS = [
  { key: 'plain', label: 'Plain' },
  { key: 'podi', label: 'Podi' },
  { key: 'karampodi', label: 'Karampodi' },
  { key: 'karam', label: 'Karam' },
];

/** Canonical lowercase dosa name from the three axes. */
export function buildDosaName({ flavor = 'plain', ghee = false, onion = false } = {}) {
  const parts = [];
  if (ghee) parts.push('ghee');
  if (flavor && flavor !== 'plain') parts.push(flavor);
  if (onion) parts.push('onion');
  const core = parts.length ? parts.join(' ') : 'plain';
  return `${core} dosa`;
}

/** Title-cased name for display, e.g. "Ghee Karampodi Onion Dosa". */
export function dosaDisplayName(spec) {
  return buildDosaName(spec).replace(/\b\w/g, c => c.toUpperCase());
}

/** Stable identity for a dosa type (for aggregation / merging identical lines). */
export function dosaKey({ flavor = 'plain', ghee = false, onion = false, name } = {}) {
  if (flavor === 'custom') return `custom|${(name || '').trim().toLowerCase()}`;
  return `${flavor}|${ghee ? 1 : 0}|${onion ? 1 : 0}`;
}

/**
 * All 16 menu dosas as one-tap specs, ordered non-ghee group then ghee group
 * (so they read as two rows). Each has { flavor, ghee, onion, key, name }.
 */
export function allDosas() {
  const flavors = ['plain', 'podi', 'karampodi', 'karam'];
  const out = [];
  [false, true].forEach(ghee =>
    flavors.forEach(flavor =>
      [false, true].forEach(onion => {
        const spec = { flavor, ghee, onion };
        out.push({ ...spec, key: dosaKey(spec), name: dosaDisplayName(spec) });
      })
    )
  );
  return out;
}

// ── Regular (Square) orders → just their tawa/griddle items ──────────────────

// Items made on the same griddle as buffet dosas — dosas, uttapams, and the
// breads the chef wants surfaced. Literal stems for the names Square spells
// consistently.
export const GRIDDLE_KEYWORDS = ['dosa', 'dose'];

/**
 * Families whose transliteration genuinely varies. Our own menu already spells
 * it "Uthappam" on the poster and "Uttapam" in the translation table, so any
 * fixed list of spellings would keep missing tickets — one pattern per family
 * covers the combinations instead. Matched against lowercased text.
 */
export const GRIDDLE_PATTERNS = [
  /(?:u|oo)t{1,2}h?ap{1,2}h?am?/i,   // uttapam, uthappam, utappam, utapam, oothappam, uttappa
  /p[ao]r{1,2}[ao]n?t{1,2}h?a/i,     // paratha, parotta, paratta, parratta, porotta, parantha
  /[pf]h?u+lk/i,                     // pulka, phulka, fulka
  /chap{1,2}[au]?t{1,2}h?i/i,        // chapathi, chapati, chappathi, chapthi, chapatti
  /pes{1,2}a?r{1,2}at/i,             // pesarattu, pessarattu, pesaratlu, masala pesarattu
  // Bounded: an unanchored "ragi" also lives inside "fragile", which is exactly
  // the kind of word that turns up in a ticket note.
  /\bragi\b/i,                       // ragi dosa, ragi roti, plain "Ragi"
];

/**
 * Is this line item a tawa/griddle item (dosa, uttapam, paratha, pulka, chapathi…)?
 * Checks the item NAME and its NOTE, so e.g. "Sunday mini buffet" with a note of
 * "plain dosa 1, karam dosa 2" is still surfaced for the dosa station.
 */
export function isGriddleItem(item) {
  const hay = `${item?.name || ''} ${item?.note || ''}`.toLowerCase();
  return GRIDDLE_KEYWORDS.some(k => hay.includes(k))
    || GRIDDLE_PATTERNS.some(re => re.test(hay));
}

/** The griddle-only line items of an order. */
export function griddleItems(order) {
  return (order?.items || []).filter(isGriddleItem);
}

/**
 * Reduce a list of regular orders to only those containing griddle items, with
 * each order's items trimmed to just those lines. Orders with none are dropped.
 *
 * Each kept item is enriched with its original index and a `done` flag derived
 * from that order's KDS `checkedItems`, so the board can strike items already
 * completed on the Live Orders screen even while the order itself is still open.
 *
 * @param {Array} orders
 * @param {Object} [checksById] — { [orderId]: { checkedItems: number[] } }
 */
export function regularGriddleOrders(orders, checksById = {}) {
  const byId = checksById || {};
  return (orders || [])
    .map(o => {
      const checked = new Set(byId[o.id]?.checkedItems || []);
      const items = (o.items || [])
        .map((it, idx) => ({ ...it, idx, done: checked.has(idx) }))
        .filter(isGriddleItem);
      return { ...o, items };
    })
    .filter(o => o.items.length > 0);
}

// ── Per-line progress ─────────────────────────────────────────────────────────

export function itemRemaining(it) {
  return Math.max(0, (it?.qty || 0) - (it?.made || 0));
}
export function itemDone(it) {
  return (it?.qty || 0) > 0 && (it?.made || 0) >= (it?.qty || 0);
}

/** Roll an order's lines into a single progress summary. */
export function orderProgress(order) {
  const items = order?.items || [];
  const total = items.reduce((s, it) => s + (it.qty || 0), 0);
  const made = items.reduce((s, it) => s + Math.min(it.made || 0, it.qty || 0), 0);
  return { total, made, remaining: Math.max(0, total - made), done: total > 0 && made >= total };
}
export function isOrderDone(order) {
  return !!order?.completedAt;
}

// ── Draft building (counter view) ─────────────────────────────────────────────

/**
 * Add a dosa spec to a draft order, merging into an existing identical line.
 * Pure — no ids are assigned here (the action layer stamps ids on persist).
 */
export function addToDraft(draft, spec, qty = 1) {
  const q = Math.max(1, Math.floor(qty) || 1);
  const key = dosaKey(spec);
  const list = (draft || []).map(d => ({ ...d }));
  const idx = list.findIndex(d => dosaKey(d) === key);
  if (idx >= 0) {
    list[idx].qty += q;
  } else {
    list.push({
      flavor: spec.flavor || 'plain',
      ghee: !!spec.ghee,
      onion: !!spec.onion,
      name: spec.name || dosaDisplayName(spec),   // custom dosas pass their own name
      qty: q,
      made: 0,
    });
  }
  return list;
}

// ── Cook board: aggregation across orders ─────────────────────────────────────

/**
 * Sum identical dosas across orders for the griddle/batch view.
 * Returns rows sorted by most-remaining first.
 */
export function aggregateDosas(orders) {
  const map = {};
  (orders || []).forEach(o => {
    (o.items || []).forEach(it => {
      const key = dosaKey(it);
      if (!map[key]) {
        map[key] = {
          key,
          name: it.name || dosaDisplayName(it),
          flavor: it.flavor || 'plain',
          ghee: !!it.ghee,
          onion: !!it.onion,
          ordered: 0,
          made: 0,
        };
      }
      map[key].ordered += it.qty || 0;
      map[key].made += Math.min(it.made || 0, it.qty || 0);
    });
  });
  return Object.values(map)
    .map(d => ({ ...d, remaining: Math.max(0, d.ordered - d.made) }))
    .sort((a, b) => b.remaining - a.remaining || a.name.localeCompare(b.name));
}

/**
 * For griddle batch-cooking: when one dosa of a type is made, credit it to the
 * longest-waiting open order that still needs it (FIFO). Returns the target
 * { orderId, itemId } to bump, or null if none remain.
 */
export function nextOrderForDosa(orders, key) {
  const candidates = (orders || [])
    .filter(o => (o.items || []).some(it => dosaKey(it) === key && itemRemaining(it) > 0))
    .sort((a, b) => (a.createdAtMs || 0) - (b.createdAtMs || 0));
  if (!candidates.length) return null;
  const o = candidates[0];
  const it = o.items.find(i => dosaKey(i) === key && itemRemaining(i) > 0);
  return { orderId: o.id, itemId: it.id };
}

// ── Day summary / counts ──────────────────────────────────────────────────────

export function summarizeDay(orders) {
  const byType = aggregateDosas(orders);
  const totalOrdered = byType.reduce((s, d) => s + d.ordered, 0);
  const totalMade = byType.reduce((s, d) => s + d.made, 0);
  const openOrders = (orders || []).filter(o => !isOrderDone(o)).length;
  return { totalOrdered, totalMade, remaining: Math.max(0, totalOrdered - totalMade), openOrders, byType };
}

/** Local date key (YYYY-MM-DD) used to scope a buffet day. */
export function todayKey(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
